import { createInterfaceId } from '@gitlab/needle';
import type { SkillConflict } from '@gitlab-org/ai-context/node';
import type { SlashCommand } from './slash_command_handler';

export type DynamicSlashCommandsResult = {
  commands: SlashCommand[];
  /** User-facing messages about commands that were skipped or failed to load. */
  warnings: string[];
  /**
   * Skill-name collisions resolved by the provider. Kept structured (rather
   * than flattened into warnings) so controllers can render them with richer
   * UI than a plain message.
   */
  conflicts?: SkillConflict[];
};

/** Providers with higher priority register first and win name collisions. */
export const SKILLS_PROVIDER_PRIORITY = 2;
export const CUSTOM_COMMANDS_PROVIDER_PRIORITY = 1;
/** Plugin commands lose collisions to skills and user-authored custom commands. */
export const PLUGIN_COMMANDS_PROVIDER_PRIORITY = 0;

/**
 * Supplies slash commands that are discovered at runtime (e.g. agent skills,
 * user/workspace custom commands) rather than being statically registered as
 * handlers. The SlashCommandService collects all providers and registers their
 * commands; each provider declares a `priority`, and higher priority wins
 * name collisions.
 */
export interface DynamicSlashCommandProvider {
  /** Higher priority providers register first and win name collisions. */
  readonly priority: number;

  getCommands(): Promise<DynamicSlashCommandsResult>;
}

export const DynamicSlashCommandProvider = createInterfaceId<DynamicSlashCommandProvider>(
  'DynamicSlashCommandProvider',
);
